<template>
  <div class="date-time pb-10">
    <p class="date-time__title">Local date and time *</p>
    <v-row>
      <v-col cols="6">
        <v-menu
          v-model="menuDateStart"
          :close-on-content-click="false"
          min-width="290px"
          offset-overflow
          offset-y
          transition="scale-transition"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              v-model="dateStart"
              :append-icon="mdiCalendarCheckOutline"
              background-color="#fff"
              class="date-time__date mb-n6"
              hide-details
              label="Start date"
              outlined
              readonly
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="dateStart"
            class="date-time__menu"
            no-title
            scrollable
            @input="menuDateStart = false"
          />
        </v-menu>
      </v-col>
      <v-col cols="6">
        <v-text-field
          v-model="timeStart"
          :append-icon="mdiClockOutline"
          background-color="#fff"
          class="date-time__time mb-n6"
          outlined
          type="time"
        />
      </v-col>
      <v-col cols="6">
        <v-menu
          v-model="menuDateEnd"
          :close-on-content-click="false"
          min-width="290px"
          offset-overflow
          offset-y
          transition="scale-transition"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              v-model="dateEnd"
              :append-icon="mdiCalendarCheckOutline"
              background-color="#fff"
              class="date-time__date"
              hide-details
              label="End date"
              outlined
              readonly
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="dateEnd"
            class="date-time__menu"
            no-title
            scrollable
            @input="menuDateEnd = false"
          />
        </v-menu>
      </v-col>
      <v-col cols="6">
        <v-text-field
          v-model="timeEnd"
          :append-icon="mdiClockOutline"
          background-color="#fff"
          class="date-time__time"
          outlined
          type="time"
        />
      </v-col>
    </v-row>
    <EventEditActionButtons
      :is-loading="isPending"
      :disabled="!isDateFilledOut"
      :error="errorMessage || error?.message"
      @next="handleNext"
      @back="$emit('back')"
      @cancel="handleCancel"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { mdiCalendarCheckOutline, mdiClockOutline } from '@mdi/js'

import { getDateFormattedForPicker, getTimeFormatted } from '@/services/date'
import EventEditActionButtons from '@/pages/CreateEventProcessingPage/ui/layout/EventEditActionButtons.vue'
import { IEventEdit } from '@/enitites/event'
import { useEventUpdateMutation } from '@/enitites/event/api/queries'

const { error, isPending, mutate: updateEvent } = useEventUpdateMutation()

const props = defineProps<{
  eventData: IEventEdit
}>()

const emit = defineEmits<{
  (e: 'next'): void
  (e: 'back'): void
  (e: 'cancel'): void
}>()

const menuDateStart = ref(false)
const menuDateEnd = ref(false)
const dateStart = ref(getDateFormattedForPicker(props.eventData.dateStart) || '')
const dateEnd = ref(getDateFormattedForPicker(props.eventData.dateEnd) || '')
const timeStart = ref(getTimeFormatted(props.eventData.dateStart) || '12:00')
const timeEnd = ref(getTimeFormatted(props.eventData.dateEnd) || '12:00')

const errorMessage = ref<string>()

function createDateTime(date: string, time: string) {
  return new Date(`${date.replace(/-/g, '/')} ${time}`)
}

const isInvalidDate = computed(() => {
  return (
    createDateTime(dateEnd.value, timeEnd.value).getTime() < createDateTime(dateStart.value, timeStart.value).getTime()
  )
})

const isDateFilledOut = computed(() => !!dateStart.value && !!dateEnd.value && !!timeStart.value && !!timeEnd.value)

function saveEventData(onSuccess: () => void) {
  if (!isDateFilledOut.value || isPending.value) return
  errorMessage.value = undefined

  if (isInvalidDate.value) {
    errorMessage.value = "End Date can't be before the Start Date"
    return
  }

  const dateStartObj = createDateTime(dateStart.value, timeStart.value)
  const dateEndObj = createDateTime(dateEnd.value, timeEnd.value)

  const isSameDay = dateStartObj.getDate() === dateEndObj.getDate()
  const dateOptions: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' }
  const timeOptions: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' }

  // Apr 5, 2023 - Apr 8, 2023
  const dateFlat = isSameDay
    ? dateStartObj.toLocaleDateString('en-US', dateOptions)
    : `${dateStartObj.toLocaleDateString('en-US', dateOptions)} - ${dateEndObj.toLocaleDateString('en-US', dateOptions)}`

  // 10:00 AM - 5:00 PM
  const timeFlat = `${dateStartObj.toLocaleTimeString('en-US', timeOptions)} - ${dateEndObj.toLocaleTimeString('en-US', timeOptions)}`

  const payload = {
    ...props.eventData,
    dateFlat,
    timeFlat,
    dateStart: dateStartObj.toISOString(),
    dateEnd: dateEndObj.toISOString(),
  }

  updateEvent(payload, { onSuccess })
}

function handleNext() {
  saveEventData(() => {
    emit('next')
  })
}

function handleCancel() {
  saveEventData(() => {
    emit('cancel')
  })
}
</script>

<style lang="scss" scoped>
@import '@/assets/style/mixins';

.date-time {
  max-width: 400px;
  margin: 60px auto 0;

  &__title {
    margin: 12px 0;
    color: var(--color-dark-800);
  }
}

.date-time__date :deep(.v-input__append-inner),
.date-time__time :deep(.v-input__append-inner) {
  position: absolute;
  right: 10px;
  background: white;
  pointer-events: none;
}

:deep(.v-text-field__slot) {
  input[type='time'] {
    justify-self: start;
    width: fit-content;
    flex: 0 0 auto;

    &::-webkit-calendar-picker-indicator {
      display: none;
    }
  }
}
</style>
